/**
 * The signed-out reader on feeds v2: what a visitor without an account sees.
 * A v2 build spends decision-model calls and takes an account, so signed out
 * the For You and feed pages never build; they show each feed's last stored
 * build, which the public feed builder refreshes every 15 minutes for the
 * public (newsletter) feeds. These turn those stored builds into the state
 * the signed-in pages already render, so the pages differ only in where their
 * state comes from.
 */
import type { BetaSearch } from '../repositories/betaSearchRepository';
import { INITIAL_STATE, type FeedV2State, type RankedStory } from './feedsV2';
import { INITIAL_FOR_YOU_V2, type ForYouV2State } from './forYouV2';

/** `GET /api/v1/feeds/<feed id>/stored`. */
export interface StoredFeedV2 {
  search_id: string;
  name: string;
  window_hours: number;
  built_at: string;
  stories: RankedStory[];
}

/** `GET /api/v1/feeds/public/for-you`. */
export interface PublicForYouV2 {
  window_hours: number;
  feeds: { search_id: string; name: string; built_at: string }[];
  /** Requested public feeds with no current build. */
  missing: string[];
  stories: RankedStory[];
}

/** What the signed-out feed page shows: a stored build (`builtAt` says how fresh), or `notBuilt`. */
export type StoredFeedV2State = FeedV2State & { builtAt: string | null; notBuilt: boolean };

/** A stored build as the feed page's finished state. */
export function feedV2StateFromStored(stored: StoredFeedV2): StoredFeedV2State {
  return { ...INITIAL_STATE, stories: stored.stories, done: true, builtAt: stored.built_at, notBuilt: false };
}

/** A feed with no stored build, signed out: nothing to show; the page asks the visitor to sign in to build it. */
export function feedV2NotBuiltState(): StoredFeedV2State {
  return { ...INITIAL_STATE, done: true, builtAt: null, notBuilt: true };
}

/**
 * The signed-out For You as the For You page's finished state; `builtAt` is its oldest feed's build.
 * A feed with no stored build yet is shown as failed, named from *interests*, so the page says its
 * stories are missing rather than that the visitor follows nothing.
 */
export function forYouV2StateFromPublic(view: PublicForYouV2, interests: BetaSearch[]): ForYouV2State & { builtAt: string | null } {
  const built = view.feeds.map(feed => feed.built_at).sort();
  const names = new Map(interests.map(feed => [feed.id, feed.name]));
  return {
    ...INITIAL_FOR_YOU_V2,
    feeds: [
      ...view.feeds.map(feed => ({ search_id: feed.search_id, name: feed.name, status: 'done' as const })),
      ...view.missing.map(id => ({ search_id: id, name: names.get(id) ?? id, status: 'failed' as const, detail: 'Not built yet' })),
    ],
    stories: view.stories,
    done: true,
    builtAt: built[0] ?? null,
  };
}

/** The feed ids a signed-out For You shows: the visitor's chips that are switched on, in order. */
export function includedFeedIds(interests: BetaSearch[]): string[] {
  return interests.filter(feed => feed.included).map(feed => feed.id);
}
